/**
 * Discovery API Integration
 * Handles workspace discovery and join request operations
 */

import axiosClient from '../axiosClient';
import {
  DiscoverWorkspacesRequest,
  DiscoverWorkspacesResponse,
  SubmitJoinRequestRequest,
  SubmitJoinRequestResponse,
  JoinWorkspaceResponse,
  WorkspaceJoinRequest,
  ApproveJoinRequestResponse,
  RejectJoinRequestResponse,
  JoinRequestQueryParams,
} from '../types/DiscoveryTypes';

/**
 * Discovery API endpoints
 */
export const discoveryApi = {
  /**
   * Discover workspaces based on search and filters
   */
  discoverWorkspaces: async (
    params?: DiscoverWorkspacesRequest,
  ): Promise<DiscoverWorkspacesResponse> => {
    try {
      const response = await axiosClient.get('/workspaces/discover', {
        params: {
          search: params?.search,
          type: params?.type,
          limit: params?.limit || 20,
          offset: params?.offset || 0,
        },
      });
      console.log('✅ Workspaces discovered:', response.data.total);
      return response.data;
    } catch (error) {
      console.error('❌ Error discovering workspaces:', error);
      throw error;
    }
  },

  /**
   * Join an open workspace instantly
   */
  joinOpenWorkspace: async (workspaceId: string): Promise<JoinWorkspaceResponse> => {
    try {
      const response = await axiosClient.post(
        `/workspaces/${workspaceId}/join`,
      );
      console.log('✅ Joined workspace:', workspaceId);
      return response.data;
    } catch (error) {
      console.error('❌ Error joining workspace:', error);
      throw error;
    }
  },

  /**
   * Submit a join request for a closed workspace
   */
  submitJoinRequest: async (
    workspaceId: string,
    data: SubmitJoinRequestRequest,
  ): Promise<SubmitJoinRequestResponse> => {
    try {
      const response = await axiosClient.post(
        `/workspaces/${workspaceId}/join-request`,
        data,
      );
      console.log('✅ Join request submitted for workspace:', workspaceId);
      return response.data;
    } catch (error) {
      console.error('❌ Error submitting join request:', error);
      throw error;
    }
  },

  /**
   * Get join requests for a workspace (admin only)
   */
  getWorkspaceJoinRequests: async (
    workspaceId: string,
    params?: JoinRequestQueryParams,
  ): Promise<WorkspaceJoinRequest[]> => {
    try {
      const response = await axiosClient.get(
        `/workspaces/${workspaceId}/join-requests`,
        {
          params: {
            status: params?.status,
          },
        },
      );
      console.log(
        '✅ Fetched join requests for workspace:',
        workspaceId,
        '- Count:',
        response.data.length,
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching workspace join requests:', error);
      throw error;
    }
  },

  /**
   * Get user's own join requests
   */
  getMyJoinRequests: async (
    params?: JoinRequestQueryParams,
  ): Promise<WorkspaceJoinRequest[]> => {
    try {
      const response = await axiosClient.get('/my-join-requests', {
        params: {
          status: params?.status,
        },
      });
      console.log('✅ Fetched my join requests - Count:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching my join requests:', error);
      throw error;
    }
  },

  /**
   * Approve a join request (admin only)
   */
  approveJoinRequest: async (
    requestId: string,
  ): Promise<ApproveJoinRequestResponse> => {
    try {
      const response = await axiosClient.patch(
        `/join-requests/${requestId}/approve`,
      );
      console.log('✅ Join request approved:', requestId);
      return response.data;
    } catch (error) {
      console.error('❌ Error approving join request:', error);
      throw error;
    }
  },

  /**
   * Reject a join request (admin only)
   */
  rejectJoinRequest: async (
    requestId: string,
  ): Promise<RejectJoinRequestResponse> => {
    try {
      const response = await axiosClient.patch(
        `/join-requests/${requestId}/reject`,
      );
      console.log('✅ Join request rejected:', requestId);
      return response.data;
    } catch (error) {
      console.error('❌ Error rejecting join request:', error);
      throw error;
    }
  },

  /**
   * Cancel a pending join request (user only)
   */
  cancelJoinRequest: async (requestId: string): Promise<void> => {
    try {
      await axiosClient.delete(`/join-requests/${requestId}`);
      console.log('✅ Join request cancelled:', requestId);
    } catch (error) {
      console.error('❌ Error cancelling join request:', error);
      throw error;
    }
  },
};

export default discoveryApi;
